/* <m-option-drawer> — one drawer per product option.
 *
 * Two constraints pull in opposite directions, and everything below exists to
 * satisfy both.
 *
 * 1. The markup must be RENDERED inside <variant-picker>. product-info.js
 *    re-fetches the section on every option change and replaces that whole
 *    element (product-info.js -> updateOptionValues -> viewTransition), so only
 *    markup living inside it gets fresh prices and availability.
 *
 * 2. The drawer must be DISPLAYED at <body> level. Anywhere inside the product
 *    section it paints under the header, and no z-index fixes it — verified on
 *    the live theme: raising the nearest stacking context to 99999 changed
 *    nothing, moving the node to <body> fixed it instantly. Neutralising the
 *    sticky ancestor and the header will-change changed nothing either. This is
 *    why every other overlay in this theme (cart drawer, search popup, quick
 *    view) is rendered at body level in layout/theme.liquid.
 *
 * So the node is portalled to <body> while open, and sent home before it
 * matters. "Before it matters" is precise: variant-picker.js reads its
 * selection with this.querySelectorAll(".m-product-option select,
 * .m-product-option input"), which only sees DESCENDANTS of <variant-picker>.
 * A radio that changes while the drawer sits at body level is invisible to it
 * and the wrong variant resolves. The change handler therefore moves the node
 * home FIRST, then re-dispatches the event from the radio, now back inside.
 */
if (!customElements.get("m-option-drawer")) {
  const openDrawers = new Set();
  const portalled = new Map(); // drawer key -> element currently at body level
  const SCROLL_LOCK = "prevent-scroll";

  /* The scroll lock is shared with the cart drawer, the search popup and the
     age verifier. Only release it when nothing of ours is open AND no other
     overlay is holding it, otherwise closing an option drawer would unlock the
     page behind an open cart drawer. */
  function releaseScrollLock() {
    if (openDrawers.size > 0) return;
    if (document.querySelector(".m-cart-drawer--active, .m-show-search")) return;
    document.documentElement.classList.remove(SCROLL_LOCK);
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class MOptionDrawer extends HTMLElement {
    connectedCallback() {
      if (!this.ready) {
        this.init();
        this.ready = true;
      }

      // Restore state after product-info.js swapped the DOM. No rAF: the drawer
      // must be open in the same frame it is inserted, so the visitor never
      // sees it blink shut.
      if (openDrawers.has(this.key) && !this.classList.contains("is-open")) {
        this.portal();
        this.wrapper.classList.remove("m:hidden");
        this.classList.add("is-open");
        document.documentElement.classList.add(SCROLL_LOCK);
        document.addEventListener("keydown", this.onKeydown);
        this.trigger?.setAttribute("aria-expanded", "true");
      }
    }

    init() {
      this.wrapper = this.querySelector(".m-drawer__wrapper");
      this.content = this.querySelector(".m-drawer__content");
      this.key = this.dataset.drawerKey;
      this.onKeydown = this.onKeydown.bind(this);

      this.trigger = this.dataset.triggerId
        ? document.getElementById(this.dataset.triggerId)
        : null;
      this.trigger?.addEventListener("click", () => this.open());

      this.querySelector("[data-option-drawer-close]")?.addEventListener(
        "click",
        () => this.close()
      );

      this.wrapper?.addEventListener("click", (event) => {
        if (event.target === this.wrapper) this.close();
      });

      this.addEventListener("change", (event) => {
        if (this.forwarding) return;
        const input = event.target;

        // The picker only sees radios that are its own descendants, so the node
        // has to be home before this event reaches it. The original event is
        // stopped and replayed from the same input once it is back inside.
        event.stopPropagation();
        this.sendHome();
        this.close();

        this.forwarding = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        this.forwarding = false;
      });
    }

    disconnectedCallback() {
      // Deliberately does NOT touch openDrawers: viewTransition removes the old
      // node 500ms after inserting the new one, so clearing state here would
      // close the drawer that was just restored. A portal move also fires this;
      // `moving` marks that case.
      if (this.moving) return;
      document.removeEventListener("keydown", this.onKeydown);
    }

    /* Move to <body> so the overlay escapes the product section paint order. */
    portal() {
      if (this.parentElement === document.body) return;

      // A previous element for this key can be stranded at body level if its
      // picker was replaced while the drawer was open. Drop it.
      const stale = portalled.get(this.key);
      if (stale && stale !== this && stale.parentElement === document.body) {
        stale.moving = true;
        stale.remove();
      }

      this.home = this.parentElement;
      this.moving = true;
      document.body.appendChild(this);
      this.moving = false;
      portalled.set(this.key, this);
    }

    sendHome() {
      portalled.delete(this.key);
      if (this.parentElement !== document.body) return;
      if (!this.home?.isConnected) return;
      this.moving = true;
      this.home.appendChild(this);
      this.moving = false;
    }

    open() {
      if (this.classList.contains("is-open")) return;

      // portal() first, openDrawers.add() after: appendChild fires
      // connectedCallback synchronously, and that callback restores is-open
      // immediately (no rAF) whenever the key is already in openDrawers, for
      // the mid-interaction case where product-info.js just swapped the
      // picker back in. Adding the key before portal() made a first, genuine
      // open take that same instant path — is-open landed in the same frame
      // as removing m:hidden, so the browser had nothing to animate from and
      // the drawer snapped open instead of sliding in.
      this.portal();
      openDrawers.add(this.key);
      this.wrapper.classList.remove("m:hidden");
      document.documentElement.classList.add(SCROLL_LOCK);
      this.trigger?.setAttribute("aria-expanded", "true");

      // One frame between "displayed" and "open" so the transform has a
      // starting value to animate from.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add("is-open"));
      });

      document.addEventListener("keydown", this.onKeydown);
      this.content?.focus();
    }

    close() {
      if (!this.classList.contains("is-open")) return;

      openDrawers.delete(this.key);
      this.classList.remove("is-open");
      this.trigger?.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", this.onKeydown);

      const wrapper = this.wrapper;
      setTimeout(() => {
        // The node may already be gone: product-info.js replaces it while the
        // close transition runs.
        if (!wrapper?.isConnected) return;
        if (this.classList.contains("is-open")) return;
        wrapper.classList.add("m:hidden");
        this.sendHome();
      }, 300);

      releaseScrollLock();
      this.trigger?.focus();
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key !== "Tab" || !this.content) return;

      const focusable = Array.from(this.content.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === this.content
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define("m-option-drawer", MOptionDrawer);
}
